import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Button as RACButton } from "react-aria-components";

import type { InsightsView } from "../../publishSearch";
import type { InsightsFilters } from "./insightsFilters";

import { BrandFilter } from "../BrandFilter";
import { AudienceInsights } from "./AudienceInsights";
import { DEFAULT_INSIGHTS_FILTERS } from "./insightsFilters";
import { InsightsFiltersPopover } from "./InsightsFiltersPopover";
import { InsightsOverview } from "./InsightsOverview";
import { PostsAnalytics } from "./PostsAnalytics";

const VIEWS: Array<{ id: InsightsView; label: string }> = [
  { id: "overview", label: "Overview" },
  { id: "posts", label: "Posts" },
  { id: "audience", label: "Audience" },
];

// A nested react-aria Tabs inside the page's TabPanel breaks its selection state, so the views are plain toggle buttons.
export function InsightsTab({ view = "overview" }: { view?: InsightsView }) {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<InsightsFilters>(
    DEFAULT_INSIGHTS_FILTERS,
  );

  function setView(nextView: InsightsView) {
    navigate({
      search: { tab: "insights", view: nextView },
      to: "/publish",
    });
  }

  return (
    <div className="flex flex-col">
      <div className="flex flex-wrap items-center justify-between gap-md border-b border-border-secondary px-xl py-md">
        <div aria-label="Insights views" className="flex gap-xs" role="group">
          {VIEWS.map(({ id, label }) => (
            <RACButton
              aria-pressed={view === id}
              className="cursor-pointer rounded-md px-md py-sm text-sm font-semibold text-text-quaternary outline-hidden hover:text-text-secondary focus-visible:ring-2 focus-visible:ring-border-brand aria-pressed:bg-bg-brand-primary aria-pressed:text-text-brand-secondary"
              key={id}
              onPress={() => setView(id)}
            >
              {label}
            </RACButton>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-md">
          <BrandFilter
            brandIds={filters.brandIds ?? []}
            onChange={(brandIds) => setFilters({ ...filters, brandIds })}
          />
          <InsightsFiltersPopover filters={filters} onChange={setFilters} />
        </div>
      </div>
      {view === "overview" && (
        <InsightsOverview
          filters={filters}
          onSeeAllPosts={() => setView("posts")}
        />
      )}
      {view === "posts" && (
        <PostsAnalytics
          filters={filters}
          onSelectPost={(postId) =>
            navigate({
              params: { postId: String(postId) },
              to: "/publish/posts/$postId",
            })
          }
        />
      )}
      {view === "audience" && <AudienceInsights filters={filters} />}
    </div>
  );
}
